// src/components/Sidebar.jsx
import React, { useEffect, useMemo, useState } from "react";
import { MdAutoAwesome, MdOutlineGroups, MdManageAccounts, MdOutlineQuestionAnswer } from "react-icons/md";
import { IoIosSettings, IoIosNotifications } from "react-icons/io";
import { CiLogout } from "react-icons/ci";
import { SlCalender } from "react-icons/sl";
import { IoInformationCircle } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { clearAdminOrganizationView } from "../utils/adminOrganizationView";

const API = process.env.REACT_APP_API_URL;

function SidebarItem({ icon, label, active, to, onClick }) {
  const navigate = useNavigate();

  function handleClick(e) {
    if (to) navigate(to);
    if (onClick) onClick(e);
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={[
        "flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm",
        active ? "bg-[#76B3FF] text-white shadow hover:bg-[#265DAB]" : "text-gray-700 hover:bg-white/70",
      ].join(" ")}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function SectionTitle({ children }) {
  return (
    <h3 className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-gray-600">
      {children}
    </h3>
  );
}

export default function Sidebar({
  active = "alle-bewohner",
}) {
  const navigate = useNavigate();

  // --- NEW: load user from backend (/me) ---
  const [userEmail, setUserEmail] = useState("");
  const [loadingUser, setLoadingUser] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingUser(true);
        const res = await fetch(`${API}/me`, { credentials: "include" });
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        if (!cancelled) setUserEmail(data.email || "");
      } catch (e) {
        if (!cancelled) {
          // keep graceful fallback; no hard failure in sidebar
          setUserEmail("");
          // optional: console.warn("Failed to load /me:", e?.message);
        }
      } finally {
        if (!cancelled) setLoadingUser(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // derive a "name" to show (since there is no name field)
  const derivedName = useMemo(() => {
    if (!userEmail) return "";
    const beforeAt = userEmail.split("@")[0] || "";
    // simple prettify: split on . or _ and capitalize
    const parts = beforeAt.split(/[._-]+/).filter(Boolean);
    if (parts.length === 0) return beforeAt;
    return parts.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(" ");
  }, [userEmail]);

  const initials = useMemo(() => {
    if (!userEmail) return "U";
    const first = userEmail.trim()[0] || "U";
    return first.toUpperCase();
  }, [userEmail]);

  async function handleLogout() {
    try {
      await fetch(`${API}/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (err) {
      console.error("Logout failed", err);
    } finally {
      clearAdminOrganizationView();
      navigate("/login", { replace: true });
    }
  }

  return (
    <aside className="sticky top-0 flex h-screen w-[260px] shrink-0 flex-col bg-[#cdeaff]">
      {/* Logo row */}
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl ring-1 ring-indigo-200">
          <img src="/steffi/logo.png" alt="SteffiCare" className="h-full w-full object-cover" />
        </div>
        <span className="text-sm font-semibold text-gray-800">SteffiCare</span>
      </div>

      <div className="mx-4 mb-4 h-px bg-black/10" />

      {/* Individual Einstellungen */}
      <div className="px-4">
        <SectionTitle>Individual Einstellungen</SectionTitle>
        <SidebarItem
          icon={<MdOutlineGroups />}
          label="Alle Bewohner"
          active={active === "alle-bewohner"}
          to="/übersicht"
        />
      </div>

      <div className="my-4 mx-4 h-px bg-black/10" />

      {/* Globale Einstellungen */}
      <div className="px-4 space-y-1">
        <SectionTitle>Grupppen Einstellungen</SectionTitle>
        <SidebarItem
          icon={<SlCalender />}
          label="Kalender"
          active={active === "kalender"}
          to="/calender"
        />
        <SidebarItem
          icon={<IoIosNotifications />}
          label="Erinnerungen"
          active={active === "erinnerungen"}
          to="/allgemeine-erinnerungen"
        />
        <SidebarItem
          icon={<MdAutoAwesome />}
          label="Aktivierungen"
          active={active === "aktivierungen"}
          to="/aktivierungen"
        />
        <SidebarItem
          icon={<MdOutlineQuestionAnswer />}
          label="Abfragen"
          active={active === "abfragen"}
          to="/abfragen"
        />
        <SidebarItem
          icon={<IoInformationCircle />}
          label="Essensplan"
          active={active === "infos"}
          to="/allgemeine-infos"
        />
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Account */}
      <div className="px-4 space-y-1">
        <SectionTitle>Account</SectionTitle>
        <SidebarItem
          icon={<MdManageAccounts />}
          label="Nutzerverwaltung"
          active={active === "user-mgmt"}
          to="/nutzerverwaltung"
        />
        <SidebarItem
          icon={<CiLogout />}
          label="Log out"
          active={active === "logout"}
          onClick={handleLogout}
        />
        <SidebarItem
          icon={<IoIosSettings />}
          label="Einstellungen"
          active={active === "settings"}
          to="/einstellungen"
        />
      </div>

      {/* User card */}
      <div className="m-3 rounded-xl border border-black/10 bg-white/80 p-3">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-200 font-semibold text-indigo-800">
            {initials}
          </div>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-gray-800">
              {loadingUser ? "…" : (derivedName || "Benutzer")}
            </div>
            <div className="truncate text-xs text-gray-500">
              {loadingUser ? "…" : (userEmail || "—")}
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}
